<template>
  <div>
    <div v-if="appAuthorized" id="app" :class="getClasses">
      <div id="wrapper" v-if="appReady">
        <the-nav
          :is-touch="$isTouchDevice"
          :hidden="$isTouchDevice && showContent"
          :keep-active-item="projectIsSelected"
        ></the-nav>
        <main>
          <router-view
            @ready="onMainContentReady"
            @before-route-leave="beforeRouteLeave"
          ></router-view>
        </main>
      </div>
      <the-main-loader :is-loading="mainLoaderIsLoading"></the-main-loader>
      <vue-progress-bar></vue-progress-bar>
    </div>
    <the-password-gate
      v-if="!appAuthorized"
      @success="onPasswordSuccess"
    ></the-password-gate>
  </div>
</template>

<script>
import Cookies from "js-cookie";
import TheNav from "@/components/TheNav.vue";
import TheMainLoader from "@/components/TheMainLoader.vue";
import ThePasswordGate from "@/components/ThePasswordGate.vue";
import { buildUrl } from "@/helpers.js";
import { mapState } from "vuex";
import constants from "@/constants";

export default {
  components: {
    TheNav,
    TheMainLoader,
    ThePasswordGate,
  },

  data() {
    return {
      appAuthorized: false,
      appReady: false,
      mainLoaderIsLoading: true,
      showContent: false,
    };
  },

  computed: {
    ...mapState(["projects", "pages"]),
    getClasses() {
      return {
        "show-content": this.showContent,
        "is-loading": this.mainLoaderIsLoading,
      };
    },
    projectIsSelected() {
      return this.$route.name == "project";
    },
  },

  methods: {
    async startApp() {
      // Start loading bar
      this.$Progress.start();

      // API call for projects and pages
      let responseProjects = await this.axios.get("api/projects");
      this.$store.commit("SET_PROJECTS", responseProjects.data);
      let responsePages = await this.axios.get("api/pages");
      this.$store.commit("SET_PAGES", responsePages.data);

      // Show the app content
      this.appReady = true;

      // Finish the progressbar animation
      this.$Progress.finish();

      // Hide the main loader
      // The 800 timeout ensures we wait for the progressbar to finish its animation
      setTimeout(() => {
        this.mainLoaderIsLoading = false;
      }, 800);

      // Preload individual projects first image
      setTimeout(() => {
        this.startLoadingImages();
      }, 2000);
    },

    onPasswordSuccess() {
      // Why scroll top top here ? because mobile browsers scroll to the focused input
      // This resulted in the page being scrolled right after the password was entered
      document.body.scrollTop = 0;

      Cookies.set("session", "bonjour", { expires: 1 });
      this.appAuthorized = true;
      this.startApp();
    },

    onMainContentReady() {
      this.showContent = true;
    },

    startLoadingImages() {
      this.projects.forEach((project) => {
        let imageSlides = project.field_slides.filter(
          (slide) => slide.type[0].target_id == "image"
        );
        if (imageSlides.length > 0) {
          const image = new Image();
          image.src = buildUrl(imageSlides[0].field_image[0].uri[0].url);
        }
      });
    },

    calculateVH() {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    },

    // This makes sure we preserve the right order for the transition.
    // We want to hide the main content from the view before we actually remove it
    beforeRouteLeave(callback) {
      // Tell the app to transition upwards. This will trigger a CSS transition
      this.showContent = false;

      // Wait for the end of the css transition, then navigate away from the route
      setTimeout(function () {
        if (callback) {
          callback();
        }
      }, 700);
    },
  },

  async created() {
    const cookie = Cookies.get("session");

    if (!constants.passwordProtected || cookie == "bonjour") {
      this.appAuthorized = true;
      this.startApp();
    }

    if (this.$isTouchDevice) {
      // Init
      this.calculateVH();

      // And listen...
      window.addEventListener("resize", this.calculateVH);
    }
  },
};
</script>

<style lang="scss">
@import "@/styles/mixins.scss";
@import "@/styles/fonts.scss";
@import url("https://use.typekit.net/xiq2dkl.css");

:root {
  // Colors
  --main-loader-background-color: #D73F13;
  --main-loader-progress-bar-color: #151515;
  --password-gate-background-color-default: #f7f6f5;
  --password-gate-background-color-focused: #D73F13;
  --password-gate-placeholder-color: #151515;
  --password-gate-text-color: #151515;
  --regular-progress-bar-color: #151515;
  --body-text-color: #151515;
  --nav-background-color: #f7f6f5;
  --nav-background-color-project-hovered: #E1DEDB;
  --nav-background-color-project-active: #183729;

  // Fonts
  --topbar-font-family: "Plain", Helvetica, Arial,
    sans-serif;
  --topbar-font-weight: 500;
  --topbar-font-size: 0.8em;
  --topbar-letter-spacing: -0.015em;
  --topbar-font-size-tablet: 0.8em;
  --topbar-font-size-mobile: 0.8em;
  --topbar-line-height: 1.1em;
  --topbar-line-height-tablet: 1.1em;
  --topbar-line-height-mobile: 1.1em;
 

  --nav-font-family: "Plain", Helvetica, Arial, sans-serif;
  --nav-font-weight: 500;
  --nav-letter-spacing: -0.05em;
  --nav-font-size: calc(1.5em + 4.2vw);
  --nav-font-size-tablet: calc(1.2em + 4.2vw);
  --nav-font-size-mobile: calc(2.3em + 4.3vw);
  --nav-line-height: 0.87em;
  --nav-line-height-tablet: 0.87em;
  --nav-line-height-mobile: 0.87em;
  --nav-link-padding-mobile: 13px 0;

  --regular-text-font-family: "Plain", Helvetica, Arial,
    sans-serif;
  --regular-text-font-weight: 500;
  --regular-text-letter-spacing: -0.035em;
  --regular-text-font-size: calc(1.7em + 1vw);
  --regular-text-font-size-tablet: calc(1.6em + 1vw);
  --regular-text-font-size-mobile: calc(1.4em + 1.7vw);
  --regular-text-line-height: 1.05em;
  --regular-text-line-height-tablet: 1.05em;
  --regular-text-line-height-mobile: 1.05em;

  --back-to-top-font-size: 5em;
  --back-to-top-font-size-tablet: 5em;
  --back-to-top-font-size-mobile: 4em;

  // Paddings
  --topbar-padding: 1vw 1.5vw;
  --topbar-padding-tablet: 1vw 1.5vw;
  --topbar-padding-mobile: 3vw;

  --nav-padding: 1vh 1.7vw 5vh 1.5vw;
  --nav-padding-tablet: 1vh 1.7vw 5vh 1.5vw;
  --nav-padding-mobile: 14vh 3vw 5vh 3vw;

  --slide-padding: 7vw 15vw;
  --slide-padding-tablet: 8vw;
  --slide-padding-mobile: 3vw;
  --slide-text-padding-mobile: 4vh 3vw;
  --slide-padding-top-mobile-first-slide: 23vh;

  --page-padding: 7vw 15vw;
  --page-padding-tablet: 8vw;
  --page-padding-mobile: 10vh 3vw;

  --back-to-top-padding: 12vh 2vw 0.5vh 2vw;
  --back-to-top-padding-tablet: 12vh 2vw 0.5vh 2vw;
  --back-to-top-padding-mobile: 4vh 3vw 1vh 3vw;;

  // Nav image sizes
  --nav-image-spacing: 15px;
  --nav-portrait-width: 135px;
  --nav-portrait-height: 176px;
  --nav-landscape-width: 210px;
  --nav-landscape-height: 135px;

  // Animations
  --small-animations-duration: 0.35s;
  --small-animations-easing: cubic-bezier(1, 0, 0.29, 1);
  --big-animations-duration: 0.7s;
  --big-animations-easing: cubic-bezier(0.7, 0, 0.3, 1);

  // Z-indexes
  --z-index-topbar: 80;
  --z-index-nav-link: 100;
  --z-index-nav-image: 120;
  --z-index-cursor-circle: 140;
  --z-index-main-loader: 200;

  // Cursor dot
  --cursor-circle-size: 10px;
  --cursor-circle-size-large-multiply-by: 4;
}

html {
  background-color: var(--loader-background-color);
}

html,
body {
  margin: 0;
  padding: 0;
  overflow: hidden;
}

a,
a:visited {
  text-decoration: none;
  color: inherit;
}

body {
  height: calc(var(--vh, 1vh) * 100);
  overflow: hidden;
  color: var(--body-text-color);

  font-family: var(--regular-text-font-family);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: geometricPrecision;
}

.regular-text {
  text-align: center;
  letter-spacing: var(--regular-text-letter-spacing);
  font-weight: var(--regular-text-font-weight);

  @include breakpoint(xs) {
    text-align: left;
    font-size: var(--regular-text-font-size-mobile);
    line-height: var(--regular-text-line-height-mobile);
  }

  @include breakpoint(sm) {
    font-size: var(--regular-text-font-size-tablet);
    line-height: var(--regular-text-line-height-tablet);
  }

  @include breakpoint(md) {
    font-size: var(--regular-text-font-size);
    line-height: var(--regular-text-line-height);
  }

  a {
    position: relative;

    &:hover {
      &:before {
        right: 0;
        left: auto;
        width: 0;
      }

      &:after {
        left: 0;
        right: auto;
        width: 100%;
      }
    }

    &:before,
    &:after {
      content: "";
      position: absolute;
      height: 1px;
      bottom: 4px;
      background-color: black;
    }

    &:before {
      transition: all var(--small-animations-duration)
        var(--small-animations-easing);
      left: 0;
      width: 100%;
    }

    &:after {
      transition: all var(--small-animations-duration)
        var(--small-animations-easing) 0.3s;
      right: 0;
      width: 0;
    }
  }
}

#app {
  width: 100%;

  &.show-content {
    #wrapper {
      transform: translate3d(0, calc(var(--vh, 1vh) * -100), 0);
    }
  }

  #wrapper {
    transition: transform var(--big-animations-duration)
      var(--big-animations-easing);
  }

  main {
    position: relative;
  }

  .__cov-progress {
    background-color: var(--regular-progress-bar-color) !important;
  }
}
</style>
